import React from "react";
import { useQuery } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { RotateCcw, ArrowRight } from "lucide-react";

export default function Reassignments() {
  const q = useQuery({
    queryKey: ["reassignments"],
    queryFn: () => api.get("/reassignments?limit=200").then(r => r.data),
    refetchInterval: 30_000,
  });

  return (
    <Layout title="Reassignment Log." subtitle="Every task the scheduler moved when a parent stalled">
      <div className="border border-white/10">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-white/10">
          <div className="col-span-3">Task</div>
          <div className="col-span-3">Reassigned from</div>
          <div className="col-span-3">To</div>
          <div className="col-span-2">When</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {q.isLoading && <div className="p-6 text-zinc-500 text-sm">Loading…</div>}
        {q.data?.length === 0 && (
          <div className="p-10 text-center">
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// nothing reassigned</div>
            <div className="text-zinc-400 text-sm">Great — no OP (parent) tasks have stalled long enough to trigger an auto-reassignment yet.</div>
          </div>
        )}
        {(q.data || []).map((t) => (
          <div key={t.id} data-testid={`reassign-row-${t.id}`}
               className="flex flex-col md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-3 border-b border-white/5 gap-2 md:gap-3 hover:bg-[#0e0e10]">
            <div className="md:col-span-3 min-w-0">
              <div className="flex items-center gap-2 text-[12px]">
                <RotateCcw className="w-3 h-3 text-amber-400 shrink-0"/>
                <span className="font-mono text-[10px] text-zinc-500">{t.id.slice(0,8)}</span>
                <span className="text-zinc-200 truncate">{t.action_type}</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 truncate">
                {t.subreddit}{t.campaign_id ? ` · c/${t.campaign_id.slice(0,6)}` : ""}
              </div>
            </div>
            <div className="md:col-span-3 min-w-0">
              <div className="text-[10px] font-mono text-zinc-500">FROM</div>
              <div className="text-[13px] text-zinc-300 truncate">
                {t.reassigned_from_agent?.name || <em className="text-zinc-600">unknown</em>}
              </div>
            </div>
            <div className="md:col-span-3 min-w-0">
              <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-1">
                <ArrowRight className="w-3 h-3"/> TO
              </div>
              <Link
                to={t.assigned_agent?.id ? `/admin/agents/${t.assigned_agent.id}` : "#"}
                data-testid={`reassign-to-agent-${t.id}`}
                className="text-[13px] text-[var(--accent)] hover:underline truncate block"
              >
                {t.assigned_agent?.name || <em className="text-zinc-600">unknown</em>}
              </Link>
            </div>
            <div className="md:col-span-2 text-[10px] font-mono text-zinc-500">
              {t.reassigned_at ? new Date(t.reassigned_at).toLocaleString("en-GB", {hour12: false}) : "-"}
            </div>
            <div className="md:col-span-1 md:text-right">
              <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${
                t.status === "Completed" ? "border-emerald-500/40 text-emerald-400"
                : t.status === "Verification Failed" ? "border-[#FF3B30]/50 text-[#FF3B30]"
                : "border-white/10 text-zinc-400"
              }`}>{t.status}</span>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
